import { useEffect, useId, useRef, useState, type Ref } from "react";
import { useInRouterContext, useSearchParams } from "react-router";
import type { PublicMapResident } from "@/shared/contracts";
import { Icon } from "@/shared/ui";
import { ResidentPortrait } from "../ResidentPortrait";
import { citiesOf, placeText } from "./publicMapModel";
import { nowLine } from "../residentView";

/** 地图标记上用的头像：身份图链（官方形象 → 身份图的退路 → 同物种插画），与公开主页同一条链。 */
function MapPortraitForMode({ resident, size, guest }: { resident: PublicMapResident; size: number; guest: boolean }) {
  return (
    <ResidentPortrait
      name={resident.name}
      species={resident.species}
      photoUrl={resident.avatar_url || resident.identity?.url || null}
      fallbackUrl={resident.identity?.fallback_url ?? null}
      official
      guestMedia={guest ? resident.avatar_version === resident.identity?.version ? { petId: resident.pet_id, identity: resident.identity } : null : undefined}
      size={size}
      surface="paper"
    />
  );
}

/** 同一处有几位（坐标完全相同）时的轻量选择：列出这一处的每一位，点谁看谁的短卡。不把坐标打散。 */
/**
 * 一处有几位时的名单。合成的那一盏（merged）里有两座以上的城、页面又给了 onPickCity 时（4b，I 10-14），先按城列人数，点城进那座城；
 * 「按名字看」切回名单（没写城市的几位写在这颗按钮上），焦点落在名单第一位。只有一座城、不是合成的，照旧直接是名单。
 */
export function SpotChooser({ residents, onPick, onClose, cardRef, merged = false, onPickCity }: { residents: PublicMapResident[]; onPick: (resident: PublicMapResident) => void; onClose: () => void; cardRef: Ref<HTMLElement>; merged?: boolean; onPickCity?: (city: string) => void }) {
  const titleId = useId();
  const { cities, unplaced } = citiesOf(residents);
  const [byName, setByName] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (byName) listRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [byName]);
  if (merged && onPickCity && cities.length >= 2 && !byName) {
    return (
      <section ref={cardRef} className="ps-pmap-card ps-pmap-chooser" role="dialog" aria-labelledby={titleId} tabIndex={-1} data-testid="map-chooser" data-chooser="city" data-motion="enter">
        <div className="ps-pmap-card__head ps-pmap-chooser__head">
          <div className="ps-pmap-card__who">
            <p className="ps-pmap-card__name" id={titleId}>
              {`这一带的 ${residents.length} 位居民`}
            </p>
            <p className="ps-pmap-chooser__where">{`在 ${cities.length} 座城里，选一座看看`}</p>
          </div>
          <button type="button" className="ps-pmap-card__close" aria-label="关上" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>
        <ul className="ps-pmap-chooser__list">
          {cities.map(({ city, count }) => (
            <li key={city}>
              <button type="button" className="ps-pmap-chooser__item ps-pmap-chooser__item--city" aria-label={`${city}，${count} 位，进这座城看看`} onClick={() => onPickCity(city)}>
                <span className="ps-pmap-chooser__text">
                  <strong>{city}</strong>
                  <small>{`${count} 位`}</small>
                </span>
                <Icon name="chevron" size={16} />
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="ps-pmap-chooser__names" onClick={() => setByName(true)}>
          {unplaced ? `按名字看（还有 ${unplaced} 位没写在哪座城）` : "按名字看"}
        </button>
      </section>
    );
  }
  return (
    <section ref={cardRef} className="ps-pmap-card ps-pmap-chooser" role="dialog" aria-labelledby={titleId} tabIndex={-1} data-testid="map-chooser" data-motion="enter">
      <div className="ps-pmap-card__head ps-pmap-chooser__head">
        <div className="ps-pmap-card__who">
          <p className="ps-pmap-card__name" id={titleId}>
            {merged ? `这一带挨得很近的 ${residents.length} 位居民` : `这一处有 ${residents.length} 位居民`}
          </p>
          <p className="ps-pmap-chooser__where">{merged ? "不在同一处，放大地图能分开看" : placeText(residents[0])}</p>
        </div>
        <button type="button" className="ps-pmap-card__close" aria-label="关上" onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
      </div>
      <ul ref={listRef} className="ps-pmap-chooser__list">
        {residents.map((resident) => (
          <li key={resident.pet_id}>
            <button type="button" className="ps-pmap-chooser__item" onClick={() => onPick(resident)}>
              <span aria-hidden="true">
                <MapPortrait resident={resident} size={40} />
              </span>
              <span className="ps-pmap-chooser__text">
                <strong>{resident.name}</strong>
                <small>{nowLine(resident)}</small>
              </span>
              <Icon name="chevron" size={16} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

type MapPortraitProps = { resident: PublicMapResident; size: number };
/** Markers, chooser and short card share the explicit route mode; standalone legacy callers stay members. */
export function MapPortrait(props: MapPortraitProps) {
  const routed = useInRouterContext();
  return routed ? <RoutedMapPortrait {...props} /> : <MapPortraitForMode {...props} guest={false} />;
}
function RoutedMapPortrait(props: MapPortraitProps) {
  const [params] = useSearchParams();
  return <MapPortraitForMode {...props} guest={params.get("view") === "guest"} />;
}
